import * as R from 'ramda'
import {
    initProductCalculationOverviewBlankFilter,
    initProductCalculationOverviewFilterList,
    ProductCalculationOverviewFilterList,
    ProductCalculationOverviewState
} from './ProductCalculationOverviewState'
import {ProductCalculationOverviewAction} from './ProductCalculationOverviewActions'
import {
    ProductCalculationOverviewFeedback,
    ProductCalculationOverviewRequestIdentifier
} from './ProductCalculationOverviewFeedback'
import {Result} from '../../Data/Result'
import {productCalculationList} from '../../Request/ProductCalculationListRequest'
import {
    ProductCalculationWithAssociations,
    productCalculationWithAssociationsDecoder
} from '../../Entities/ProductCalculationWithAssociations'
import {Session} from '../../Data/Session'
import {decodeHttpResponse, HttpError} from '../../Data/Http'
import {update} from '../../Data/ObjectHelper'
import * as ProductCalculationResource from '../../Request/ProductCalculationResource'
import {array, Decoder} from 'json-bouncer'
import {messageWithTranslationDecoder} from '../../Entities/MessageWithTranslation.entity.evolve'
import {Paginated, paginatedDecoder} from '../../Data/Paginated'
import * as Pagination from '../../Data/Pagination'
import * as PageSection from '../../Data/PageSection'
import {HttpRequest} from '../../Data/HttpRequest'
import {FeedbackLoop} from '../../Data/FeedbackLoop'

const initialItemsPerPage = 25

export function activate(session: Session): HttpRequest {
    return productCalculationList(
        session.manufacturer.id,
        {offset: 0, limit: initialItemsPerPage},
        initProductCalculationOverviewFilterList()
    )
}

export function initialDecoder(): Decoder<Paginated<ProductCalculationWithAssociations>> {
    return paginatedDecoder(productCalculationWithAssociationsDecoder())
}

export function init(
    productCalculationsWithAssociations: Paginated<ProductCalculationWithAssociations>,
): ProductCalculationOverviewState {
    return {
        content:
            Pagination.fromPaginated(
                {itemsPerPage: initialItemsPerPage, currentPage: 1},
                productCalculationsWithAssociations,
            ),
        filterOpen: false,
        searchValue: '',
        exactFilter: false,
        setAttribute: 'type',
        filterSuggestions: [],
        filter: initProductCalculationOverviewFilterList(),
        error: null,
    }
}

type ProductCalculationOverviewLoop =
    FeedbackLoop<ProductCalculationOverviewState, ProductCalculationOverviewFeedback>

export function reduce(
    session: Session,
    state: ProductCalculationOverviewState,
    action: ProductCalculationOverviewAction,
): ProductCalculationOverviewLoop {
    switch (action.type) {
        case 'MenuButtonClicked':
            return {
                state,
                feedback: [{type: 'OpenDrawer'}],
            }
        case 'ItemsPerPageChanged':
            return requestProductCalculationList(
                session,
                {
                    state: update(
                        {
                            content:
                                Pagination.setItemsPerPage(
                                    action.value,
                                    state.content,
                                ),
                        },
                        state),
                    feedback: [],
                },
            )
        case 'PaginationNavigation':
            return requestProductCalculationList(
                session,
                {
                    state: update(
                        {
                            content:
                                Pagination.navigate(
                                    action.navigation,
                                    state.content,
                                ),
                        },
                        state,
                    ),
                    feedback: []
                }
            )
        case 'OpenFilter':
            return {
                state: update({filterOpen: true}, state),
                feedback: [],
            }
        case 'CloseFilter':
            return {
                state: update({filterOpen: false}, state),

                feedback: [],
            }
        case 'ProductCalculationListResponse':
            const result =
                decodeHttpResponse(
                    paginatedDecoder(productCalculationWithAssociationsDecoder()),
                    action.result)
            return (() => {
                switch (result.type) {
                    case 'Ok':
                        return {
                            state: update(
                                {
                                    content:
                                        Pagination.setData(
                                            result.value,
                                            state.content,
                                        ),
                                },
                                state,
                            ),
                            feedback: [],
                        }
                    case 'Err':
                        return {
                            state: update({error: result.error}, state),
                            feedback: [],
                        }
                }
            })()
        case 'ToogleExactFilterChecked':
            return {
                state: update({
                    exactFilter: !state.exactFilter
                }, state),
                feedback: []
            }
        case 'FilterSearchValueChanged':
            return {
                state: update({
                    searchValue: action.value
                }, state),
                feedback: []
            }
        case 'FilterAttributeChanged':
            return {
                state: update({
                    setAttribute: action.attribute
                }, state),
                feedback: []
            }
        case 'ClearFilterMessageSuggestions':
            return {
                state: update({
                    filterSuggestions: []
                }, state),
                feedback: []
            }
        case 'FetchFilterMessageSuggestions':
            return {
                state,
                feedback: [{
                    type: 'SendRequest',
                    identifier: {
                        type: 'ProductCalculationOverviewFilterSuggestion',
                        key: action.key
                    },
                    httpRequest:
                        ProductCalculationResource.listMessageSuggestions(
                            session.manufacturer.id,
                            action.key,
                            action.value,
                        ),
                }]
            }
        case 'FetchFilterMessageSuggestionResponse':
            const messages =
                decodeHttpResponse(
                    array(messageWithTranslationDecoder()),
                    action.result)
            return (messages.type === 'Ok')
                ? {
                    state: update({
                            filterSuggestions: messages.value
                        },
                        state),
                    feedback: []
                }
                : {
                    state,
                    feedback: []
                }
        case 'FilterApplySuggestion':
            return {
                state,
                feedback: []
            }
        case 'ApplyFilter':
            if (state.searchValue === '' || state.setAttribute === '') {
                return {
                    state,
                    feedback: []
                }
            }
            return requestProductCalculationList(
                session,
                {
                    state:
                        update(
                            {
                                filter: update(
                                    {
                                        [state.setAttribute]: update(
                                            {
                                                value: state.searchValue,
                                                exact: state.exactFilter
                                            },
                                            state.filter[state.setAttribute],
                                        )
                                    },
                                    state.filter,
                                ),
                                searchValue: '',
                                setAttribute:
                                    getNextNotFilteredAttribute(
                                        state.filter,
                                        state.setAttribute,
                                    )
                            },
                            state,
                        ),
                    feedback: []
                }
            )
        case 'ClearFilter':
            return requestProductCalculationList(
                session,
                {
                    state:
                        update(
                            {
                                filter: update(
                                    {
                                        [action.key]: update(
                                            {value: null, exact: false},
                                            state.filter[action.key],
                                        )
                                    },
                                    state.filter,
                                ),
                                setAttribute: action.key,
                            },
                            state),
                    feedback: []
                }
            )
        case 'ClearAllFilter':
            return requestProductCalculationList(
                session,
                {
                    state:
                        update(
                            {
                                filter: update(
                                    {
                                        type: initProductCalculationOverviewBlankFilter(),
                                        format: initProductCalculationOverviewBlankFilter(),
                                        coverage: initProductCalculationOverviewBlankFilter(),
                                        print: initProductCalculationOverviewBlankFilter(),
                                        paper: initProductCalculationOverviewBlankFilter(),
                                        processing: initProductCalculationOverviewBlankFilter(),
                                    },
                                    state.filter,
                                ),
                                setAttribute:
                                    state.setAttribute === ''
                                        ? 'type'
                                        : state.setAttribute
                            },
                            state),
                    feedback: []
                }
            )
    }
}

function requestProductCalculationList(
    session: Session,
    {state, feedback}: ProductCalculationOverviewLoop
): ProductCalculationOverviewLoop {
    return {
        state,
        feedback:
            R.append<ProductCalculationOverviewFeedback>(
                {
                    type: 'SendRequest',
                    identifier: {
                        type: 'ProductCalculationList'
                    },
                    httpRequest:
                        productCalculationList(
                            session.manufacturer.id,
                            PageSection.toOffsetLimit(
                                state.content.pageSection,
                            ),
                            state.filter
                        )
                },
                feedback,
            )
    }
}

export function httpResponseAction(
    identifier: ProductCalculationOverviewRequestIdentifier,
    result: Result<HttpError, string>,
): ProductCalculationOverviewAction {
    switch (identifier.type) {
        case 'ProductCalculationList':
            return {
                type: 'ProductCalculationListResponse',
                result,
            }
        case 'ProductCalculationOverviewFilterSuggestion':
            return {
                type: 'FetchFilterMessageSuggestionResponse',
                key: identifier.key,
                result: result,
            }
    }
}

function getNextNotFilteredAttribute(
    filter: ProductCalculationOverviewFilterList,
    currentlySelected: string
) {
    const list = ['type', 'format', 'coverage', 'print', 'paper', 'processing']
    let processableFilters: string[] = R.filter(
        (element: string) => {
            return filter[element].value === null
        },
        list
    )
    if (processableFilters.length <= 1) {
        return ''
    }
    let currentIndex = R.findIndex(
        (element: string) => {
            return (element === currentlySelected)
        },
        processableFilters
    )
    if (currentIndex + 1 < processableFilters.length) {
        return processableFilters[currentIndex + 1]
    }
    return processableFilters[0]
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/Pages/ProductCalculationOverview/ProductCalculationOverviewReducer.ts